/**
 * Where the open panel goes, from the space the page has: never from the device's name.
 *
 * The canvas keeps its 9:16 proportions everywhere (the totem's look, 1:1 on the totem). A panel goes beside it when
 * the canvas, at the full height, leaves at least a readable panel's width; otherwise it goes below, as a sheet: closed
 * it fills the band a full-width canvas leaves free (a phone is taller than 9:16), open it rises over the canvas.
 */
import { CANVAS } from '../layout/model';

/** A panel narrower than this is not readable beside the canvas: it goes below. */
export const SIDE_MIN = 360;
/** The widest a side panel gets; the canvas keeps the rest. */
export const SIDE_MAX = 560;
/** The closed sheet is never lower than this: a transport bar fits, a thumb reaches it. */
export const PEEK_MIN = 120;
/** Half open is never lower than this: the transport and some turns fit, even on a small phone. */
export const HALF_MIN = 320;
/** The open sheet leaves this much of the canvas in sight above it. */
export const FULL_GAP = 56;

export type SheetState = 'peek' | 'half' | 'full';
export const SHEET_STATES: readonly SheetState[] = ['peek', 'half', 'full'];

export interface Viewport {
  width: number;
  height: number;
}

export type PageLayout =
  | { placement: 'none' }
  | { placement: 'side'; panelWidth: number }
  | { placement: 'bottom'; sheetHeight: number; peekHeight: number };

const ASPECT = CANVAS.width / CANVAS.height;

/** The panel's place and size for ``viewport``; ``open`` false: no panel at all. */
export function pageLayout(viewport: Viewport, open: boolean, sheet: SheetState = 'peek'): PageLayout {
  if (!open) return { placement: 'none' };
  const besideCanvas = viewport.width - viewport.height * ASPECT;
  if (besideCanvas >= SIDE_MIN) return { placement: 'side', panelWidth: Math.round(Math.min(SIDE_MAX, besideCanvas)) };
  const peekHeight = Math.round(Math.max(PEEK_MIN, viewport.height - viewport.width / ASPECT));
  const heights: Record<SheetState, number> = {
    peek: peekHeight,
    half: Math.min(viewport.height - FULL_GAP, Math.max(peekHeight, HALF_MIN, Math.round(viewport.height / 2))),
    full: Math.max(peekHeight, viewport.height - FULL_GAP),
  };
  return { placement: 'bottom', sheetHeight: heights[sheet], peekHeight };
}

/** The sheet state nearest to a dragged height. */
export function nearestSheet(viewport: Viewport, height: number): SheetState {
  let best: SheetState = 'peek';
  let distance = Infinity;
  for (const state of SHEET_STATES) {
    const layout = pageLayout(viewport, true, state);
    const h = layout.placement === 'bottom' ? layout.sheetHeight : 0;
    if (Math.abs(h - height) < distance) [best, distance] = [state, Math.abs(h - height)];
  }
  return best;
}
